/**
 * Turning a courier and a number into something you can click.
 *
 * Kyu ruled out a courier feed and he was right: an integration per carrier,
 * credentials to hold, and a background job that goes quiet without anybody
 * noticing — for a handful of orders a quarter. A date somebody types is more
 * honest than a feed nobody maintains, because it is obvious when it is
 * missing.
 *
 * What a feed would have bought cheaply is the link. For the four carriers
 * everyone actually uses, the tracking URL is the number in a known template,
 * so no integration is needed to reach the courier's own page.
 *
 * An explicitly stored URL always wins. The templates are a convenience, not a
 * claim to know every carrier — an unrecognised one simply renders as text
 * until somebody pastes the link.
 */

interface Carrier {
  /** Matched case-insensitively against the courier name as typed. */
  match: RegExp;
  url: (tracking: string) => string;
}

const CARRIERS: Carrier[] = [
  {
    match: /\bdhl\b/i,
    url: (n) => `https://www.dhl.com/en/express/tracking.html?AWB=${encodeURIComponent(n)}`,
  },
  {
    match: /\bfedex\b/i,
    url: (n) => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}`,
  },
  { match: /\bups\b/i, url: (n) => `https://www.ups.com/track?tracknum=${encodeURIComponent(n)}` },
  {
    match: /\busps\b/i,
    url: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(n)}`,
  },
];

/**
 * Where to follow this shipment, or null when there is nowhere to send anyone.
 *
 * Null rather than a guessed URL: a link that 404s on a courier's site reads
 * as the shipment being lost, which is a considerably worse thing to imply
 * than "we do not have a link".
 */
export function trackingLink(
  courier: string,
  tracking: string | null,
  explicitUrl: string,
): string | null {
  const url = explicitUrl.trim();
  // Only https. The stored value is constrained at the database, but this
  // function is also handed values from payloads, and a javascript: URL
  // rendered into an href is the one way a tracking field becomes dangerous.
  if (url.startsWith('https://')) {
    return url;
  }

  const number = (tracking ?? '').trim();
  if (number === '') {
    return null;
  }
  const carrier = CARRIERS.find((entry) => entry.match.test(courier));
  return carrier === undefined ? null : carrier.url(number);
}

/** "DHL · JD0002", or just the number when nobody said which courier. */
export function trackingLabel(courier: string, tracking: string | null): string {
  const number = (tracking ?? '').trim();
  const name = courier.trim();
  if (number === '') {
    return '';
  }
  return name === '' ? number : `${name} · ${number}`;
}
